import type {
  Household,
  HouseholdAmenityAccessRestriction,
  HouseholdAmenitySummary,
} from "../types";

import {AMENITY_RESTRICTION_REFERRAL_LEAD} from "@/src/modules/properties/lib/property-manager-contact";

import type {RestrictedAmenityKey} from "./restrictable-amenities";
import {formatRestrictedUntilDisplay} from "./restrictable-amenities";

/** Staff-facing fallback when property manager contact is unavailable (e.g. error toasts). */
export const GENERIC_AMENITY_RESTRICTION_MESSAGE = AMENITY_RESTRICTION_REFERRAL_LEAD;

export type AmenityRestrictionCheckSource = {
  amenity_access_restricted?: boolean;
  restricted_amenities?: string[];
  restricted_until?: string | null;
};

export function getHouseholdAmenityRestriction(
  raw: HouseholdAmenityAccessRestriction | null | undefined,
): HouseholdAmenityAccessRestriction | null {
  if (raw == null) return null;
  return raw;
}

export function getHouseholdAmenityAccessListDisplay(
  household: Pick<
    Household,
    "amenity_access_restricted" | "amenity_access_restriction" | "restricted_amenities" | "restricted_until"
  >,
): {
  restricted: boolean;
  reason: string | null;
  restrictedAt: string | null;
  restrictedAmenities: string[];
  restrictedUntil: string | null;
} {
  const restricted = isHouseholdAmenityRestricted(household);
  const restriction = getHouseholdAmenityRestriction(household.amenity_access_restriction);
  const restrictedAmenities =
    restriction?.restricted_amenities ??
    (Array.isArray(household.restricted_amenities) ? household.restricted_amenities : []);
  const restrictedUntil =
    restriction?.restricted_until ?? household.restricted_until ?? null;

  return {
    restricted,
    reason: restriction?.internal_reason?.trim() || null,
    restrictedAt: restriction?.restricted_at?.trim() || null,
    restrictedAmenities,
    restrictedUntil,
  };
}

/** Household-level: any amenity blocked (list badge, banner flag). */
export function isHouseholdAmenityRestricted(
  source:
    | Pick<Household, "amenity_access_restricted">
    | HouseholdAmenitySummary
    | {amenity_access_restricted?: boolean}
    | null
    | undefined,
): boolean {
  return source?.amenity_access_restricted === true;
}

/** Per-amenity check for create blocking and resource-scoped UI. */
export function isAmenityKeyRestricted(
  source: AmenityRestrictionCheckSource | null | undefined,
  amenityKey: RestrictedAmenityKey,
): boolean {
  if (!source) return false;

  const amenities = source.restricted_amenities;
  if (Array.isArray(amenities)) {
    if (amenities.length > 0) {
      return amenities.includes(amenityKey);
    }
    return false;
  }

  // Per-resource nested household: boolean is scoped to that amenity.
  return source.amenity_access_restricted === true;
}

export function getRestrictionHistoryStatusBadge(status: string): {
  label: string;
  variant: "error" | "secondary";
} {
  const normalized = status.trim().toLowerCase();
  if (normalized === "active") {
    return {label: "Restricted", variant: "error"};
  }
  if (normalized === "reversed") {
    return {label: "Lifted", variant: "secondary"};
  }
  return {label: status, variant: "secondary"};
}

export function getRestrictedUntilLabel(until: string | null | undefined): string {
  return formatRestrictedUntilDisplay(until);
}

/** Resident-safe message from household show/embed (`amenity_access_resident_message`). */
export function getHouseholdAmenityAccessResidentMessage(
  household:
    | Pick<Household, "amenity_access_resident_message" | "amenity_access_restriction">
    | null
    | undefined,
): string | null {
  const embedMessage = household?.amenity_access_resident_message?.trim();
  if (embedMessage) return embedMessage;
  return household?.amenity_access_restriction?.resident_message?.trim() || null;
}

/** Row-level flag from list/show APIs (`amenity_access_restricted` on row or nested `household`). */
export type AmenityRestrictionRowSource = {
  amenity_access_restricted?: boolean;
  amenity_access_restricted_at?: string | null;
  restricted_until?: string | null;
  household?: {
    amenity_access_restricted?: boolean;
    amenity_access_restricted_at?: string | null;
    restricted_until?: string | null;
  } | null;
};

export function resolveRowAmenityAccessRestricted(
  source: AmenityRestrictionRowSource | null | undefined,
): boolean {
  if (source?.amenity_access_restricted === true) return true;
  return isHouseholdAmenityRestricted(source?.household);
}

export function resolveRowAmenityAccessRestrictedAt(
  source: AmenityRestrictionRowSource | null | undefined,
): string | null {
  const rowAt = source?.amenity_access_restricted_at?.trim();
  if (rowAt) return rowAt;
  const householdAt = source?.household?.amenity_access_restricted_at?.trim();
  return householdAt || null;
}

export function resolveRowAmenityAccessRestrictedUntil(
  source: AmenityRestrictionRowSource | null | undefined,
): string | null {
  const rowUntil = source?.restricted_until?.trim();
  if (rowUntil) return rowUntil;
  const householdUntil = source?.household?.restricted_until?.trim();
  return householdUntil || null;
}
